/**
 * <s-slideshow> — Horizontal scroll-snap slideshow with dots, arrows, autoplay,
 * and reduced-motion / visibility / focus-aware pause behavior.
 *
 * Commands (dispatched via commandfor/command on descendant buttons):
 *   --next          — Advance one slide (wraps).
 *   --prev          — Go back one slide (wraps).
 *   --select-slide  — Jump to a specific slide (source must be a generated dot button).
 *   --pause         — Stop autoplay and set aria-live="polite".
 *   --play          — Start autoplay and set aria-live="off".
 *
 * Required child markup:
 *   [data-slideshow-track]  — Scrollable track containing slides (required; no-op without it).
 *   [data-slide]             — Each slide element inside the track.
 *   [data-slideshow-prev]    — Previous-slide button (hidden when total <= 1).
 *   [data-slideshow-next]    — Next-slide button (hidden when total <= 1).
 *   [data-slideshow-dots]    — Container where dot buttons are generated at runtime.
 *   [data-slideshow-play]    — Play button (shown while paused).
 *   [data-slideshow-pause]   — Pause button (shown while playing).
 *
 * Element attributes/dataset (on <s-slideshow> itself or on [data-slideshow-dots]):
 *   autoplay                      — Present to enable autoplay on mount.
 *   autoplay-interval             — Interval in ms (default 5000).
 *   initial-slide                 — Index of the initial slide (default 0).
 *   data-slide-of-template        — i18n template for slide aria-label / sr announcement.
 *                                   Uses "{{ current }}" and "{{ total }}" placeholders.
 *   data-playing-text             — i18n string announced when autoplay starts.
 *   data-paused-template          — i18n template announced when autoplay stops
 *                                   ("{{ current }}", "{{ total }}" placeholders).
 *   [data-slideshow-dots][data-dot-label-template] — i18n template for each dot aria-label.
 *                                                    Uses "__INDEX__" and "__TOTAL__" placeholders.
 *
 * Generated markup:
 *   Dot <button>s inside [data-slideshow-dots] with [commandfor=<id>] + [command="--select-slide"].
 *
 * External coupling:
 *   #sr-announcer (global) — live region for slide/play-pause announcements.
 *   Dispatches 'slideshow:change' CustomEvent with { index, total }.
 */
class SSlideshow extends HTMLElement {
  #ac;
  #current = 0;
  #observer;
  #autoplayTimer;
  #autoplaySuspended = false;
  #isHovered = false;
  #isFocused = false;
  #track;
  #dots;
  #slides = [];
  #slideIndexMap = new Map();
  #dotIndexMap = new Map();
  #announceRafId = 0;
  #scrollAnimId = 0;
  #selectRafId = 0;
  #reorderOuterRafId = 0;
  #reorderInnerRafId = 0;
  #reordering = false;
  #playBtn;
  #pauseBtn;
  #prefersReducedMotion;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.#prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#track = this.querySelector('[data-slideshow-track]');

    // Cache slides and pre-compute index maps to avoid DOM queries and parseInt in hot paths
    if (this.#track) {
      this.#slides = Array.from(this.#track.querySelectorAll('[data-slide]'));
      for (let i = 0; i < this.#slides.length; i++) {
        this.#slideIndexMap.set(this.#slides[i], i);
      }
    }

    const total = this.#slides.length;

    // Assign slide indices and aria-labels from JS (no longer passed as Liquid params)
    const slideOfTemplate = this.dataset.slideOfTemplate;
    for (let i = 0; i < total; i++) {
      const slide = this.#slides[i];
      slide.dataset.slideIndex = i;
      if (slideOfTemplate) {
        slide.setAttribute('aria-label', slideOfTemplate
          .replace('{{ current }}', String(i + 1))
          .replace('{{ total }}', String(total)));
      }
    }

    // Generate dot pagination buttons dynamically
    this.#generateDots(total);

    // Hide arrows and dots container when there is only 1 slide
    if (total <= 1) {
      const prevBtn = this.querySelector('[data-slideshow-prev]');
      const nextBtn = this.querySelector('[data-slideshow-next]');
      const dotsContainer = this.querySelector('[data-slideshow-dots]');
      if (prevBtn) prevBtn.hidden = true;
      if (nextBtn) nextBtn.hidden = true;
      if (dotsContainer) dotsContainer.hidden = true;
    }

    // Cache play/pause buttons
    this.#playBtn = this.querySelector('[data-slideshow-play]');
    this.#pauseBtn = this.querySelector('[data-slideshow-pause]');

    // Command handler
    this.addEventListener('command', this.#handleCommand, opts);

    // IntersectionObserver on slides — tracks which is current
    this.#setupVisibilityObserver();

    // Autoplay
    if (this.hasAttribute('autoplay') && !this.#prefersReducedMotion.matches) {
      this.#startAutoplay();
    } else if (this.#track) {
      this.#track.setAttribute('aria-live', 'polite');
    }
    this.#prefersReducedMotion.addEventListener('change', (e) => {
      if (e.matches) {
        this.#clearAutoplay();
        if (this.#track) this.#track.setAttribute('aria-live', 'polite');
      }
    }, opts);

    // Autoplay pause on hover/focus — track separate flags so pointerleave
    // doesn't resume while keyboard focus is still inside, and vice-versa.
    this.addEventListener('pointerenter', () => {
      this.#isHovered = true;
      this.#suspendAutoplay();
    }, opts);
    this.addEventListener('pointerleave', () => {
      this.#isHovered = false;
      if (!this.#isFocused) this.#resumeAutoplay();
    }, opts);
    this.addEventListener('focusin', (e) => {
      if (!this.contains(e.relatedTarget)) {
        this.#isFocused = true;
        this.#suspendAutoplay();
      }
    }, opts);
    this.addEventListener('focusout', (e) => {
      if (!this.contains(e.relatedTarget)) {
        this.#isFocused = false;
        if (!this.#isHovered) this.#resumeAutoplay();
      }
    }, opts);
    document.addEventListener('visibilitychange', () => {
      document.hidden ? this.#suspendAutoplay() : this.#resumeAutoplay();
    }, opts);

    // Initial slide
    this.#selectInitialSlide();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
    this.#clearAutoplay();
    cancelAnimationFrame(this.#announceRafId);
    cancelAnimationFrame(this.#scrollAnimId);
    cancelAnimationFrame(this.#selectRafId);
    cancelAnimationFrame(this.#reorderOuterRafId);
    cancelAnimationFrame(this.#reorderInnerRafId);
  }

  #handleCommand = (e) => {
    switch (e.command) {
      case '--next':
        this.next();
        break;
      case '--prev':
        this.previous();
        break;
      case '--select-slide': {
        const index = this.#dotIndexMap.get(e.source);
        if (index !== undefined) {
          this.select(index);
        }
        break;
      }
      case '--pause':
        this.#clearAutoplay();
        this.removeAttribute('autoplay');
        if (this.#track) this.#track.setAttribute('aria-live', 'polite');
        this.#updatePlayPauseButton();
        this.#announcePlayPause(false);
        break;
      case '--play':
        this.setAttribute('autoplay', '');
        this.#startAutoplay();
        this.#updatePlayPauseButton();
        this.#announcePlayPause(true);
        break;
    }
  };

  #generateDots(total) {
    const dotsContainer = this.querySelector('[data-slideshow-dots]');
    if (!dotsContainer || total <= 1) {
      this.#dots = [];
      return;
    }

    const slideshowId = this.id;
    const labelTemplate = dotsContainer.dataset.dotLabelTemplate || '';
    const fragment = document.createDocumentFragment();

    for (let i = 0; i < total; i++) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.dataset.slideIndex = i;
      btn.setAttribute('commandfor', slideshowId);
      btn.setAttribute('command', '--select-slide');
      btn.setAttribute('aria-label', labelTemplate
        .replace('__INDEX__', String(i + 1))
        .replace('__TOTAL__', String(total)));
      btn.className = 'cursor-pointer relative flex items-center justify-center min-h-touch-target min-w-3 md:min-w-5 before:block before:size-1.5 before:rounded-full before:transition-colors before:bg-on-surface/30 hover:before:bg-on-surface/60 data-active:before:bg-accent data-active:before:scale-125 focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-2 motion-safe:transition-transform motion-safe:active:scale-[0.93]';
      fragment.appendChild(btn);
    }

    // Build dot array and index map from fragment before appending to DOM
    // (avoids a post-append querySelectorAll + parseInt)
    this.#dots = Array.from(fragment.children);
    for (let i = 0; i < this.#dots.length; i++) {
      this.#dotIndexMap.set(this.#dots[i], i);
    }

    dotsContainer.appendChild(fragment);
  }

  #setupVisibilityObserver() {
    if (!this.#track || this.#slides.length === 0) return;

    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          const index = this.#slideIndexMap.get(entry.target);
          if (index !== undefined && index !== this.#current) {
            this.#updateState(index);
          }
        }
      }
    }, {
      root: this.#track,
      threshold: 0.5,
    });

    for (const slide of this.#slides) {
      this.#observer.observe(slide);
    }
  }

  async select(index) {
    if (index < 0 || index >= this.#slides.length) return;
    if (this.#reordering) return;

    const targetSlide = this.#slides[index];
    const currentSlide = this.#slides[this.#current];
    const isAdjacent = Math.abs(index - this.#current) <= 1;

    if (isAdjacent || this.#prefersReducedMotion.matches) {
      // Normal case: adjacent slide or reduced-motion.
      // Update state synchronously (dots, aria) so the UI responds instantly,
      // but defer the layout-read scroll to the next frame to keep the
      // event handler fast (INP optimization).
      this.#updateState(index);
      this.#selectRafId = requestAnimationFrame(() => {
        const left = targetSlide.offsetLeft
          - (this.#track.offsetWidth - targetSlide.offsetWidth) / 2;
        this.#animateScrollTo(left);
      });
      return;
    }

    // Non-adjacent wrap: temporarily reorder the DOM so the target slide
    // is next to the current slide, scroll one slide over (smooth), then
    // restore the DOM. The user sees a normal directional scroll.
    //
    // Layout optimization: batch all DOM writes first, then defer reads
    // to the next frame to avoid forced synchronous layout recalc.
    this.#reordering = true;
    this.#observer?.disconnect();

    const isWrapForward = this.#current === this.#slides.length - 1 && index === 0;
    const isWrapBack = this.#current === 0 && index === this.#slides.length - 1;
    const forward = isWrapForward || (!isWrapBack && index > this.#current);

    // Read phase — cache width before DOM mutations
    const slideWidth = targetSlide.offsetWidth;

    // Write phase — DOM mutations only, no reads
    const placeholder = document.createElement('div');
    placeholder.setAttribute('data-slide', '');
    placeholder.style.cssText = `width:${slideWidth}px;flex-shrink:0`;
    targetSlide.before(placeholder);

    if (forward) {
      currentSlide.after(targetSlide);
    } else {
      currentSlide.before(targetSlide);
    }

    // Update aria/dots immediately so the UI responds before the animation plays
    this.#updateState(index);

    // Deferred read+scroll — let the browser do one layout pass for the
    // DOM changes, then read positions and scroll in the next frame.
    // Wrap in try/finally so #reordering and DOM state are always cleaned
    // up, even if the element disconnects mid-animation (AbortError).
    try {
      await new Promise((resolve, reject) => {
        const onAbort = () => reject(new DOMException('disconnected', 'AbortError'));
        this.#ac.signal.addEventListener('abort', onAbort, { once: true });
        this.#reorderOuterRafId = requestAnimationFrame(() => {
          this.#track.scrollLeft = currentSlide.offsetLeft
            - (this.#track.offsetWidth - currentSlide.offsetWidth) / 2;
          this.#reorderInnerRafId = requestAnimationFrame(() => {
            this.#ac.signal.removeEventListener('abort', onAbort);
            resolve();
          });
        });
      });

      if (!this.isConnected) return;

      const targetLeft = targetSlide.offsetLeft
        - (this.#track.offsetWidth - targetSlide.offsetWidth) / 2;
      await this.#animateScrollTo(targetLeft);

      if (!this.isConnected) return;

      // Restore DOM without ever invalidating the scroll position.
      // Insert target back first (track keeps its full width), correct
      // scrollLeft while the track is still oversized, then remove the
      // placeholder. This avoids the browser clamping scrollLeft into
      // an out-of-range value and flashing the wrong slide.
      placeholder.before(targetSlide);
      this.#track.scrollLeft = targetSlide.offsetLeft
        - (this.#track.offsetWidth - targetSlide.offsetWidth) / 2;
      placeholder.remove();
    } catch {
      // Disconnected mid-animation — restore DOM to original order
      placeholder.before(targetSlide);
      placeholder.remove();
    } finally {
      this.#reordering = false;
      for (const slide of this.#slides) this.#observer?.observe(slide);
    }
  }

  #animateScrollTo(targetLeft, duration = 400) {
    cancelAnimationFrame(this.#scrollAnimId);
    this.#scrollAnimId = 0;

    if (this.#prefersReducedMotion.matches) {
      this.#track.scrollLeft = targetLeft;
      return Promise.resolve();
    }

    const start = this.#track.scrollLeft;
    const delta = targetLeft - start;
    if (Math.abs(delta) < 1) return Promise.resolve();

    // Disable snap during animation — mandatory snap overrides each
    // frame's scrollLeft assignment when we drive the scroll ourselves.
    this.#track.style.scrollSnapType = 'none';

    const t0 = performance.now();
    return new Promise((resolve) => {
      const onAbort = () => {
        this.#track.style.scrollSnapType = '';
        resolve();
      };
      this.#ac.signal.addEventListener('abort', onAbort, { once: true });

      const step = (now) => {
        const p = Math.min((now - t0) / duration, 1);
        this.#track.scrollLeft = start + delta * this.#easeDrawer(p);
        if (p < 1) {
          this.#scrollAnimId = requestAnimationFrame(step);
        } else {
          this.#scrollAnimId = 0;
          this.#track.style.scrollSnapType = '';
          this.#ac.signal.removeEventListener('abort', onAbort);
          resolve();
        }
      };
      this.#scrollAnimId = requestAnimationFrame(step);
    });
  }

  // cubic-bezier(0.32, 0.72, 0, 1) — matches var(--ease-drawer)
  // Newton-Raphson: solve x(u) = t for parameter u, return y(u).
  #easeDrawer(t) {
    let u = t;
    for (let i = 0; i < 8; i++) {
      const x = u * (0.96 + u * (-1.92 + u * 1.96)) - t;
      const dx = 0.96 + u * (-3.84 + u * 5.88);
      if (Math.abs(dx) < 1e-6) break;
      u -= x / dx;
    }
    u = Math.max(0, Math.min(1, u));
    return u * (2.16 + u * (-1.32 + u * 0.16));
  }

  next() {
    const total = this.#slides.length;
    if (total === 0) return;

    let nextIndex = this.#current + 1;
    if (nextIndex >= total) nextIndex = 0;

    this.select(nextIndex);
  }

  previous() {
    const total = this.#slides.length;
    if (total === 0) return;

    let prevIndex = this.#current - 1;
    if (prevIndex < 0) prevIndex = total - 1;

    this.select(prevIndex);
  }

  #updateState(index) {
    this.#current = index;
    if (this.#slides.length === 0) return;

    // Update aria-hidden and inert on slides using cached index map
    for (const slide of this.#slides) {
      const isActive = this.#slideIndexMap.get(slide) === index;
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      if (isActive) {
        slide.removeAttribute('inert');
      } else {
        slide.setAttribute('inert', '');
      }
    }

    // Update dot pagination using cached index map
    for (const dot of this.#dots) {
      const isActive = this.#dotIndexMap.get(dot) === index;
      if (isActive) {
        dot.setAttribute('aria-current', 'true');
        dot.setAttribute('data-active', '');
      } else {
        dot.removeAttribute('aria-current');
        dot.removeAttribute('data-active');
      }
    }

    // Dispatch custom event
    this.dispatchEvent(new CustomEvent('slideshow:change', {
      detail: { index, total: this.#slides.length },
      bubbles: true,
    }));

    // Screen reader announcement
    this.#announce(index, this.#slides.length);
  }

  #announce(index, total) {
    const announcer = document.getElementById('sr-announcer');
    if (!announcer) return;

    const template = this.dataset.slideOfTemplate;
    if (!template) return;
    const text = template
      .replace('{{ current }}', String(index + 1))
      .replace('{{ total }}', String(total));

    // Cancel any pending rAF to prevent stacking if called rapidly
    cancelAnimationFrame(this.#announceRafId);

    // Double-set pattern for screen reader re-announcement
    announcer.textContent = '';
    this.#announceRafId = requestAnimationFrame(() => {
      announcer.textContent = text;
    });
  }

  #announcePlayPause(isPlaying) {
    const announcer = document.getElementById('sr-announcer');
    if (!announcer) return;

    let text;
    if (isPlaying) {
      text = this.dataset.playingText || '';
    } else {
      const template = this.dataset.pausedTemplate;
      if (template) {
        const total = this.#slides.length;
        text = template
          .replace('{{ current }}', String(this.#current + 1))
          .replace('{{ total }}', String(total));
      } else {
        text = '';
      }
    }

    if (!text) return;

    // Cancel any pending rAF to prevent stacking if called rapidly
    cancelAnimationFrame(this.#announceRafId);

    // Double-set pattern for screen reader re-announcement
    announcer.textContent = '';
    this.#announceRafId = requestAnimationFrame(() => {
      announcer.textContent = text;
    });
  }

  #startAutoplay() {
    this.#clearAutoplay();
    const interval = parseInt(this.getAttribute('autoplay-interval'), 10) || 5000;
    this.#autoplaySuspended = false;
    if (this.#track) this.#track.setAttribute('aria-live', 'off');
    this.#autoplayTimer = setInterval(() => {
      this.next();
    }, interval);
  }

  #clearAutoplay() {
    if (this.#autoplayTimer) {
      clearInterval(this.#autoplayTimer);
      this.#autoplayTimer = null;
    }
  }

  #suspendAutoplay() {
    this.#clearAutoplay();
    this.#autoplaySuspended = true;
    if (this.#track) this.#track.setAttribute('aria-live', 'polite');
  }

  #resumeAutoplay() {
    if (this.#isHovered || this.#isFocused) return;
    if (this.#autoplaySuspended && this.hasAttribute('autoplay') && !this.#prefersReducedMotion.matches) {
      this.#autoplaySuspended = false;
      this.#startAutoplay();
    }
  }

  #updatePlayPauseButton() {
    const isPlaying = this.hasAttribute('autoplay');

    if (this.#playBtn) this.#playBtn.hidden = isPlaying;
    if (this.#pauseBtn) this.#pauseBtn.hidden = !isPlaying;
  }

  #selectInitialSlide() {
    const initial = parseInt(this.getAttribute('initial-slide'), 10);
    if (!Number.isNaN(initial) && initial > 0 && initial < this.#slides.length) {
      // Update aria/dots synchronously so they reflect the correct slide immediately
      this.#updateState(initial);
      // Defer the layout-reading scroll to rAF to avoid forced reflow during connectedCallback
      this.#selectRafId = requestAnimationFrame(() => {
        const slide = this.#slides[initial];
        this.#track.scrollTo({ left: slide.offsetLeft - (this.#track.offsetWidth - slide.offsetWidth) / 2, behavior: 'instant' });
      });
    } else {
      // Slide 0 is the default — no scroll needed, just set state
      this.#updateState(0);
    }
  }
}

customElements.define('s-slideshow', SSlideshow);
